import { useEffect, useMemo } from "react";

import {
  CurrentPosition,
  DeviceData,
  DeviceHealthData,
  ModelAlertSettings,
  Service,
} from "../../../API";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetDevicesByLocationOrTag } from "../../data-hub/components/liveview/hooks/useGetDevicesByLocationOrTag";
import { useListenToDeviceDataChange } from "../../deployment-health/hooks/useListenToDeviceDataChange";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { useGetLazyPaginatedDevicesMakes } from "../../system-settings/tabs/devices/hooks/useGetLazyPaginatedDevicesMakes";
import { DisplayNameInput, servicesByNodeVariable } from "../variables/devices";
import { useGetServicesByLocation } from "./useGetServicesByLocation";

export interface IModel {
  serviceName: ServiceTypeEnum;
  serviceId: string;
  nodeId: string;
  isRunning?: boolean;
  configuration?: string;
  modelAlertSettingsId?: string;
  modelAlertSettings?: ModelAlertSettings;
}

export type DeviceDataType = Omit<DeviceData, "__typename">;

export interface IDeviceTableRow {
  rowId: string;
  name: string;
  location: DisplayNameInput;
  node: DisplayNameInput;
  makeModelId: string;
  models: IModel[];
  modelsToRun: string[];
  deviceData: DeviceDataType;
  currentPosition?: CurrentPosition | null;
  healthData: Omit<DeviceHealthData, "__typename">;
  isOnline: boolean;
  cameraIpAddress: string;
  rtspTemplate: string;
  skuNumber?: string | null;
  configurationName?: string | null;
  serialNumber?: string | null;
  componentSerialNumber?: string | null;
}

interface IUseDeviceTableRows {
  rows: IDeviceTableRow[];
  loading: boolean;
}

// this hook gets devices by location or tag locations &&& their respective services &&& their respective deviceMakes (to render compatiable models)

// get all devices -> location or customer -> tags
// get all services -> location (does not work for tags!) -> tags
// get all deviceMakes (ok for scale)
export const useDeviceTableRows = (): IUseDeviceTableRows => {
  const selectedLocation = useSelectedLocationDropdown();

  const {
    devices,
    loading: deviceLoading,
    refetch,
  } = useGetDevicesByLocationOrTag();

  const { getCachedLocation } = useLocationFromCache();

  const { data: devicesMakesData, loading: devicesMakesDataLoading } =
    useGetLazyPaginatedDevicesMakes();

  const { data: deviceHealthData } = useListenToDeviceDataChange();

  // Fetch services for the selected location (single query instead of N queries per device)
  const { servicesByDeviceId, loading: servicesLoading } =
    useGetServicesByLocation(selectedLocation?.value);

  const isLoading = deviceLoading || devicesMakesDataLoading || servicesLoading;

  // Update the servicesByLocationVariable with all services for backward compatibility

  const rowsByLocation: IDeviceTableRow[] = useMemo(() => {
    const mappedData = !isLoading
      ? devices?.map(item => {
          const cachedLocation = getCachedLocation(item?.locationId ?? "");

          const foundDeviceMake = devicesMakesData?.getDevicesMakes.items.find(
            el => el?.model === item?.makeModelId
          );

          // Get services for this device from the pre-fetched map
          const deviceServices =
            servicesByDeviceId.get(item?.id as string) ?? [];

          // Convert services to IModel format
          const models: IModel[] = deviceServices.map(
            (service: Service): IModel => ({
              serviceName: service.serviceType as ServiceTypeEnum,
              serviceId: service.id as string,
              nodeId: service.nodeId as string,
              isRunning: service.isRunning ?? false,
              configuration: service.configuration ?? undefined,
              modelAlertSettingsId: service.modelAlertSettingsId ?? undefined,
              modelAlertSettings: service.modelAlertSettings ?? undefined,
            })
          );

          return {
            rowId: item?.id as string,
            name: item?.name as string,
            location: {
              id: item?.locationId as string,
              name: cachedLocation?.name as string,
            },
            node: {
              id: item?.nodeId as string,
              name: item?.nodeName as string,
            },
            modelsToRun: (foundDeviceMake?.modelsToRun ?? []).filter(
              (model): model is string => model !== null
            ),
            models,
            makeModelId: item?.makeModelId as string,
            deviceData: item?.deviceData,
            currentPosition: item?.currentPosition,
            healthData: item?.healthData ?? "{}",
            isOnline: Boolean(item?.healthData?.isOnline),
            cameraIpAddress: item?.cameraIpAddress as string,
            rtspTemplate: "", // TODO: Add rtspTemplate to Device type if needed
            skuNumber: item?.skuNumber,
            configurationName: item?.configurationName,
            serialNumber: item?.serialNumber,
            componentSerialNumber: item?.componentSerialNumber,
          };
        }) ?? []
      : [];

    let rows = [...mappedData];

    if (selectedLocation?.value) {
      rows = rows?.filter(item => {
        const nameOfLocation = item.location.id;

        return nameOfLocation === selectedLocation?.value;
      });
    }

    return rows;
  }, [
    devices,
    window.location.hash,
    isLoading,
    selectedLocation?.value,
    servicesByDeviceId,
  ]);

  // Note: Tag-based service fetching is now handled by useGetServicesByDevices
  // which fetches services for all devices regardless of location or tag filtering

  useEffect(() => {
    return () => {
      // Clear the servicesByLocation variable when the component is unmounted
      servicesByNodeVariable([]);
    };
  }, []);

  const rows = rowsByLocation;

  const memoizedDevicesRows =
    useMemo(() => {
      const isDeviceDataFromSubscription = deviceHealthData?.listenToDeviceData;

      if (!isDeviceDataFromSubscription) return rows;

      const updatedRows = rows.map(device => {
        if (isDeviceDataFromSubscription.id === device?.rowId) {
          // Check if the online status has changed
          if (
            isDeviceDataFromSubscription?.healthData?.isOnline !==
            device?.healthData?.isOnline
          ) {
            refetch();
          }

          return {
            ...device,
            isOnline: Boolean(
              isDeviceDataFromSubscription?.healthData?.isOnline
            ),
          };
        }

        return {
          ...device,
        };
      });

      return updatedRows;
    }, [deviceHealthData, rows]) ?? [];

  const sortedRows = [...memoizedDevicesRows]
    ?.sort((a, b) => {
      if (!a.skuNumber && !b.skuNumber) {
        return 0;
      }

      if (!a.skuNumber) {
        return 1;
      }

      if (!b.skuNumber) {
        return -1;
      }

      return a.skuNumber?.localeCompare(b.skuNumber);
    })
    .map((item, i) => ({ ...item, sequenceNumber: i + 1 }));

  return {
    rows: sortedRows,
    loading: isLoading,
  };
};
